import { Link } from "@tanstack/react-router";
import {
	ArrowRight,
	BadgeCheck,
	CalendarClock,
	ClipboardCheck,
	FileCheck2,
	HeartPulse,
	KeyRound,
	type LucideIcon,
	Ruler,
	ShieldCheck,
	Smartphone,
	Snowflake,
	Users,
} from "lucide-react";
import type React from "react";
import { NightDecor } from "@/components/blocks/decor/Decor";
import { ExpoCost } from "@/components/blocks/scenes/CostCompare";
import { CostCurves } from "@/components/blocks/scenes/CostCurves";
import { CardGrid } from "@/components/blocks/sections/CardGrid";
import { Cta } from "@/components/blocks/sections/Cta";
import { Facts } from "@/components/blocks/sections/Facts";
import { Faq } from "@/components/blocks/sections/Faq";
import { ListCard } from "@/components/blocks/sections/ListCard";
import { NumberedPoints } from "@/components/blocks/sections/NumberedPoints";
import { PageHero } from "@/components/blocks/sections/PageHero";
import { Section } from "@/components/blocks/sections/Section";
import { SectionHeading } from "@/components/blocks/sections/SectionHeading";
import { StickyStory } from "@/components/blocks/sections/StickyStory";
import { ServiceHeroActions } from "@/components/blocks/services/ServiceHeroActions";
import { Reveal, RevealGroup, RevealItem } from "@/components/motion/motion";
import { useSectionViewTracking } from "@/lib/analytics";
import { COSTS } from "@/lib/costs";
import { useI18n } from "@/lib/i18n/use-i18n";
import { USE_CASE_KEYS, USE_CASES, type UseCaseKey } from "@/lib/use-cases";
import { DayTimeline } from "./DayTimeline";
import { CleaningScene } from "./scenes/CleaningScene";
import { HvacScene } from "./scenes/HvacScene";
import { QhseScene } from "./scenes/QhseScene";
import type { UseCaseSceneProps } from "./scenes/StageScene";
import { TakeoverScene } from "./scenes/TakeoverScene";
import { PortalRevenue, QualityTrend } from "./UseCaseVisuals";

const SECTION_IDS = {
	day: "journee",
	stakes: "enjeux",
	build: "solution",
	why: "pourquoi",
	scope: "premiere-version",
	faq: "questions",
	related: "autres-cas",
	cta: "contact",
} as const;

const SECTIONS = Object.values(SECTION_IDS).map((id) => ({ id, label: id }));

/** The product scene of each use case: cycles in the hero, follows the story. */
export const USE_CASE_SCENES: Record<
	UseCaseKey,
	(props: UseCaseSceneProps) => React.ReactNode
> = {
	hvac: HvacScene,
	qhse: QhseScene,
	cleaning: CleaningScene,
	takeover: TakeoverScene,
};

const STAKE_ICONS: Record<string, LucideIcon> = {
	attestation: FileCheck2,
	fluids: Snowflake,
	schedule: CalendarClock,
	duerp: ClipboardCheck,
	iso: BadgeCheck,
	prevention: ShieldCheck,
	transfer: Users,
	measure: Ruler,
	health: HeartPulse,
	android: Smartphone,
	ios: Smartphone,
	ownership: KeyRound,
};

/** The economic argument of each use case, beside its numbered reasons. */
function WhyVisual({ useCase }: { useCase: UseCaseKey }) {
	const { dict } = useI18n();
	switch (useCase) {
		case "hvac": {
			const copy = dict.useCases.items.hvac.why.visual;
			return (
				<div className="space-y-4">
					<CostCurves
						saas={COSTS.useCases.hvac}
						title={copy.title}
						subscriptionName={copy.subscription}
					/>
					<p className="text-sm text-pretty text-muted-foreground">
						{copy.note}
					</p>
				</div>
			);
		}
		case "qhse":
			return <PortalRevenue />;
		case "cleaning":
			return <QualityTrend />;
		case "takeover":
			return <ExpoCost />;
	}
}

/**
 * One trade or situation, told the same way on every use-case page: a
 * typical day, what the trade requires, the tool step by step, why custom,
 * the first version, objections, then the other use cases.
 */
export function UseCasePage({ useCase }: { useCase: UseCaseKey }) {
	const { dict, locale, localeParams } = useI18n();
	useSectionViewTracking(SECTIONS, { pageType: "use_case", locale });
	const common = dict.useCases.common;
	const page = dict.useCases.items[useCase];
	const { need } = USE_CASES[useCase];
	const Scene = USE_CASE_SCENES[useCase];
	const placement = `use_case_${useCase}`;

	return (
		<>
			<PageHero
				label={page.hero.label}
				title={page.hero.title}
				emphasis={page.hero.emphasis}
				intro={page.hero.intro}
				visual={<Scene className="mx-auto max-w-[34rem]" />}
				footer={
					<div className="mx-auto w-full max-w-7xl px-6 pb-14 lg:px-10">
						<Facts items={page.facts} />
					</div>
				}
			>
				<ServiceHeroActions
					need={need}
					placement={`${placement}_hero`}
					primary={page.hero.primaryCta}
					secondary={page.hero.secondaryCta}
					processId={SECTION_IDS.build}
				/>
			</PageHero>

			<Section id={SECTION_IDS.day}>
				<SectionHeading
					index="01"
					label={page.day.label}
					title={page.day.title}
					description={page.day.description}
				/>
				<DayTimeline
					className="mt-14"
					moments={page.day.moments}
					frictionLabel={common.frictionLabel}
				/>
			</Section>

			<Section id={SECTION_IDS.stakes} tone="stone">
				<SectionHeading
					index="02"
					label={page.stakes.label}
					title={page.stakes.title}
					description={page.stakes.description}
				/>
				<CardGrid
					className="mt-14"
					items={Object.entries(page.stakes.items).map(([key, item]) => ({
						key,
						title: item.title,
						description: item.description,
						icon: STAKE_ICONS[key],
						details: [{ label: common.referenceLabel, text: item.ref }],
					}))}
				/>
			</Section>

			<Section id={SECTION_IDS.build}>
				<SectionHeading
					index="03"
					label={page.build.label}
					title={page.build.title}
					description={page.build.description}
				/>
				<StickyStory
					className="mt-16 md:mt-24"
					steps={Object.entries(page.build.steps).map(([key, step]) => ({
						key,
						title: step.title,
						description: step.description,
					}))}
					renderVisual={(index) => <Scene stage={index} steps={false} />}
				/>
			</Section>

			<Section id={SECTION_IDS.why} tone="night" decor={<NightDecor />}>
				<SectionHeading
					index="04"
					label={page.why.label}
					title={page.why.title}
					description={page.why.description}
				/>
				<div className="mt-14 grid items-start gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] lg:gap-16">
					<Reveal>
						<WhyVisual useCase={useCase} />
					</Reveal>
					<NumberedPoints items={page.why.items} />
				</div>
			</Section>

			<Section id={SECTION_IDS.scope}>
				<div className="grid gap-5 lg:grid-cols-2">
					<ListCard
						label={page.scope.first.label}
						title={page.scope.first.title}
						items={page.scope.first.items}
					/>
					<ListCard
						label={page.scope.check.label}
						title={page.scope.check.title}
						items={page.scope.check.items}
						className="bg-muted/60"
					/>
				</div>
			</Section>

			<Section id={SECTION_IDS.faq} tone="stone">
				<Faq title={page.faq.title} items={page.faq.items} />
			</Section>

			<Section id={SECTION_IDS.related}>
				<SectionHeading
					label={common.related.label}
					title={common.related.title}
				/>
				<RevealGroup as="ul" className="mt-12 grid gap-4 md:grid-cols-3">
					{USE_CASE_KEYS.filter((key) => key !== useCase).map((key) => {
						const card = dict.useCases.items[key].card;
						return (
							<RevealItem as="li" key={key}>
								<Link
									to={USE_CASES[key].to}
									params={localeParams}
									className="group flex h-full flex-col rounded-2xl border bg-card p-6 transition-[border-color,transform] duration-300 ease-(--ease-soft) hover:-translate-y-0.5 hover:border-foreground/20 md:p-7"
								>
									<span className="font-mono text-[11px] tracking-[0.07em] text-muted-foreground uppercase">
										{card.name}
									</span>
									<span className="mt-4 text-lg leading-snug font-semibold tracking-tight text-balance">
										{card.title}
									</span>
									<span className="mt-auto flex items-center gap-2 pt-6 font-mono text-[13px] text-signal-ink">
										{common.related.link}
										<ArrowRight className="size-4 transition-transform duration-300 ease-(--ease-soft) group-hover:translate-x-1" />
									</span>
								</Link>
							</RevealItem>
						);
					})}
				</RevealGroup>
				<Link
					to="/{-$locale}/cas-usage"
					params={localeParams}
					className="mt-8 inline-flex border-b border-signal/60 pb-0.5 font-mono text-[13px] transition-colors hover:border-signal"
				>
					{common.related.all}
				</Link>
			</Section>

			<Cta
				id={SECTION_IDS.cta}
				{...page.cta}
				need={need}
				placement={`${placement}_cta`}
			/>
		</>
	);
}
